import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import isArray from 'lodash/isArray';
import moment from 'moment';
import React, { FC, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider, QueryClient } from 'react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Timestamp } from 'api/models';
import { TaskDrawer } from 'components/organisms/task-drawer';
import { CKEditorContext } from 'contexts';
import { CurrentUserProvider, AccountProvider } from 'contexts/providers';
import { TaskPaneContextProvider } from 'contexts/providers/task-pane';
import { store } from 'data';
import { localeData } from 'translations/en-moment';
import translations from 'translations/en.json';
import {
  InsertResourcelistCommandOptions,
  InsertResourcelistRenderProps,
} from 'vendor/ckeditor/plugins/resource-list-section/types';
import { ResourceListSection } from '../resource-section';

export interface ResourceSectionProps {
  config: InsertResourcelistCommandOptions;
  projectId?: number;
}

export const ResourceSection: FC<ResourceSectionProps> = ({
  config,
  projectId,
}) => {
  const locale = 'en';

  useLayoutEffect(() => {
    moment.updateLocale(locale, localeData);
  }, [locale]);

  return (
    <AccountProvider>
      <CurrentUserProvider>
        <ResourceListSection config={config} projectId={projectId} />
      </CurrentUserProvider>
    </AccountProvider>
  );
};
const queryClient = new QueryClient();

// This is used by CK to render the resource section list
export const resourceSectionRenderer = (
  {
    resourceSectionId,
    resourceIds,
    resourceSectionType,
    resourceSectionFilterType,
    resourceSectionFilterRange,
  }: InsertResourcelistRenderProps,
  container: HTMLElement,
  editor: Editor,
  extraContextData,
  projectId?: number,
): void => {
  const locale = 'en';

  const resourceIdsArray = isArray(resourceIds)
    ? resourceIds
    : resourceIds?.split(',').map((id) => Number(id));

  const resourceFilterRangeArr = isArray(resourceSectionFilterRange)
    ? resourceSectionFilterRange
    : resourceSectionFilterRange?.split(',') ?? [0, 0];
  const resourceFilterRange = [
    Number(resourceFilterRangeArr[0]),
    Number(resourceFilterRangeArr[1]),
  ] as [Timestamp, Timestamp];

  ReactDOM.render(
    <Provider store={store}>
      <BrowserRouter>
        <TaskPaneContextProvider>
          <IntlProvider locale={locale} messages={translations}>
            <QueryClientProvider client={queryClient}>
              <CKEditorContext.Provider value={editor}>
                <TaskDrawer />
                <ResourceSection
                  config={{
                    resourceSectionId,
                    resourceSectionType,
                    resourceIds: resourceIdsArray,
                    resourceSectionFilterType,
                    resourceSectionFilterRange: resourceFilterRange,
                    extraContextData,
                  }}
                  projectId={projectId}
                />
              </CKEditorContext.Provider>
            </QueryClientProvider>
          </IntlProvider>
        </TaskPaneContextProvider>
      </BrowserRouter>
    </Provider>,
    container,
  );
};
